import { Alert, Button, Group, Stack, Text } from '@mantine/core'
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconDownload,
  IconInfoCircle,
  IconSend,
} from '@tabler/icons-react'
import { SUBMIT_PERMISSION_TOOLTIP, SIGNATURE_REQUIRED_TOOLTIP } from './constants'
import { TooltipButton } from './TooltipButton'

type Props = {
  title: string
  subtitle: string
  onBack: () => void
  onPreview: () => void
  onSubmit: () => void
  exporting: boolean
  submitting: boolean
  orderSubmitted: boolean
  canSubmit?: boolean
  canOrderLoading?: boolean
  hideActions?: boolean
  submitTooltip?: string
  labError?: string | null
  orgError?: string | null
  warnings?: string[]
  isSigned?: boolean
}

export const FormHeader = ({
  title,
  subtitle,
  onBack,
  onPreview,
  onSubmit,
  exporting,
  submitting,
  orderSubmitted,
  canSubmit = true,
  canOrderLoading = false,
  hideActions = false,
  submitTooltip,
  labError,
  orgError,
  warnings,
  isSigned = true,
}: Props) => {
  const effectiveCanSubmit = canOrderLoading ? true : canSubmit
  const isSubmitDisabled =
    orderSubmitted || !effectiveCanSubmit || !!labError || !!orgError || !isSigned

  const disabledReason = !effectiveCanSubmit
    ? submitTooltip || SUBMIT_PERMISSION_TOOLTIP
    : !isSigned
      ? SIGNATURE_REQUIRED_TOOLTIP
      : labError
        ? labError
        : orgError
          ? orgError
          : null

  return (
    <>
      <Group justify="space-between" mb="md" wrap="wrap">
        <Group gap="sm">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
            Back
          </Button>
          <div>
            <Text size="xl" fw={700}>
              {title}
            </Text>
            <Text size="sm" c="dimmed">
              {subtitle}
            </Text>
          </div>
        </Group>
        {!hideActions && (
          <Group gap="sm">
            <Button
              variant="outline"
              leftSection={<IconDownload size={16} />}
              onClick={onPreview}
              loading={exporting}
            >
              Download PDF
            </Button>
            <TooltipButton
              color="teal"
              leftSection={<IconSend size={16} />}
              loading={submitting}
              disabled={isSubmitDisabled}
              disabledReason={disabledReason}
              onClick={onSubmit}
            >
              {orderSubmitted ? 'Order Submitted' : 'Submit Order'}
            </TooltipButton>
          </Group>
        )}
      </Group>
      {(labError || orgError || (warnings && warnings.length > 0)) && (
        <Stack gap="xs" mb="md">
          {orgError && (
            <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
              {orgError}
            </Alert>
          )}
          {labError && (
            <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
              {labError}
            </Alert>
          )}
          {warnings?.map((warning, i) => (
            <Alert key={i} icon={<IconInfoCircle size={16} />} color="yellow" variant="light">
              {warning}
            </Alert>
          ))}
        </Stack>
      )}
    </>
  )
}
